import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import defaultProfileImg from '../../../../../assets/images/default_profile_img.svg';
import { ReactComponent as WarningIcon } from '../../../../../assets/images/warningIcon.svg';
import Button from '../../../../../components/shared/Button/Button';
import Checkbox from '../../../../../components/shared/Checkbox/Checkbox';
import CustomPasswordField from '../../../../../components/shared/CustomPasswordField/CustomPasswordField';
import CustomTextAreaField from '../../../../../components/shared/CustomTextAreaField/CustomTextAreaField';
import Modal from '../../../../../components/shared/Modal/Modal';
import { IsMobile } from '../../../../../helpers/IsMobile';
import { validatePassword } from '../../../../../redux/actions/authActions';

import styles from './CloseAccountModal.module.scss';

const OTHER_REASON = 6;

const CloseAccountModal = ({ onClose, handleSubmit }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const [modalPage, setModalPage] = useState('first');
  const [reasons, setReasons] = useState([]);
  const [otherReason, setOtherReason] = useState('');
  const [pass, setPass] = useState('');
  const [repeatPass, setRepeatPass] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const userDetails = useSelector((state) => state.userReducer.user);
  const userId = useSelector((state) => state.authReducer.currentUser.user?.id);

  const modalFullScreen = IsMobile();

  const handleReasons = useCallback(
    ({ target: { checked, value } }) =>
      checked
        ? setReasons((currReasons) => [...currReasons, +value])
        : setReasons((currReasons) => currReasons.filter((val) => val !== +value)),
    []
  );

  return (
    <>
      <Modal show={modalPage === 'first'} onHide={onClose} size="lg" centered fullscreen={modalFullScreen}>
        <Modal.CustomHeader closeButton>
          <Modal.Title>{t('closeAccount.closingBoonAccount')}</Modal.Title>
        </Modal.CustomHeader>

        <Modal.Body>
          <div className={styles.modalContainer}>
            <span className={styles.modalTitle}>{t('closeAccount.whyLeaving')}</span>
            <span>{t('closeAccount.sorryToSee')}</span>
            <div>
              <Checkbox
                type="checkbox"
                onChange={handleReasons}
                id={1}
                value={0}
                label={t('closeAccount.tooManyEmails')}
              />
              <Checkbox type="checkbox" onChange={handleReasons} id={2} value={1} label={t('closeAccount.noValue')} />
              <Checkbox
                type="checkbox"
                onChange={handleReasons}
                id={3}
                value={2}
                label={t('closeAccount.unwantedContact')}
              />
              <Checkbox
                type="checkbox"
                onChange={handleReasons}
                id={4}
                value={3}
                label={t('closeAccount.privacyConcern')}
              />
              <Checkbox
                type="checkbox"
                onChange={handleReasons}
                id={5}
                value={4}
                label={t('closeAccount.duplicateAccount')}
              />
              <Checkbox
                type="checkbox"
                onChange={handleReasons}
                id={6}
                value={5}
                label={t('closeAccount.dontUseEnough')}
              />
              <Checkbox type="checkbox" onChange={handleReasons} id={7} value={6} label={t('dispute.other')} />
            </div>

            {reasons.includes(OTHER_REASON) && (
              <CustomTextAreaField
                value={otherReason}
                rows={5}
                onChange={(e) => setOtherReason(e.target.value)}
                label={t('description')}
              />
            )}
          </div>
        </Modal.Body>

        <div className={styles.modalFooter}>
          <Modal.CustomFooter>
            <Button variant="primary" onClick={onClose}>
              {t('closeAccount.backToSettings')}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => setModalPage('second')}
              disabled={reasons.includes(6) && !otherReason}
            >
              {t('next')}
            </Button>
          </Modal.CustomFooter>
        </div>
      </Modal>

      <Modal show={modalPage === 'second'} onHide={onClose} size="lg" centered fullscreen={modalFullScreen}>
        <Modal.CustomHeader closeButton>
          <Modal.Title>{t('closeAccount.closeYourAccount')}</Modal.Title>
        </Modal.CustomHeader>

        <Modal.Body>
          <div className={styles.modalContainer}>
            <div className={styles.userDetails}>
              <img
                src={userDetails.avatar_thumb ?? defaultProfileImg}
                alt="profile-img"
                className={styles.profileImage}
              />
              <div className={styles.profileInfo}>
                <span className={styles.userName}>{userDetails.full_name}</span>
                <span className={styles.greyed}>{userDetails.full_address}</span>
                <span className={styles.greyed}>{userDetails.company_position}</span>
              </div>
            </div>

            <span>{t('closeAccount.securityReason')}</span>
            <div>
              <CustomPasswordField
                onBlur={() => setShowValidation(true)}
                onFocus={() => setShowValidation(false)}
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                label={t('closeAccount.enterPassword')}
              />
              <CustomPasswordField
                onBlur={() => setShowValidation(true)}
                onFocus={() => setShowValidation(false)}
                value={repeatPass}
                onChange={(e) => setRepeatPass(e.target.value)}
                label={t('closeAccount.reenterPassword')}
              />
            </div>
            {pass !== repeatPass && (
              <span className={showValidation ? 'd-block' : 'd-none'}>{t('account.passwordsDontMatch')}</span>
            )}
          </div>
        </Modal.Body>

        <div className={styles.modalFooter}>
          <Modal.CustomFooter>
            <Button variant="primary" onClick={onClose}>
              {t('closeAccount.backToSettings')}
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => dispatch(validatePassword(userId, pass, () => setModalPage('third')))}
              disabled={!(pass && repeatPass && pass === repeatPass)}
            >
              {t('account.closeAccount')}
            </Button>
          </Modal.CustomFooter>
        </div>
      </Modal>

      <Modal show={modalPage === 'third'} onHide={onClose} size="lg" centered fullscreen={modalFullScreen}>
        <Modal.CustomHeader closeButton>
          <Modal.Title>{t('account.closeAccount')}</Modal.Title>
        </Modal.CustomHeader>

        <Modal.Body>
          <div className={styles.modalContainer}>
            <div className={styles.finalConfirmation}>
              <WarningIcon color="#EA5506" />
              <span className={styles.header}>{t('closeAccount.closeYourAccount')}</span>
              <span>{t('closeAccount.confirmCloseAccount')}</span>
            </div>
          </div>
        </Modal.Body>

        <div className={styles.modalFooter}>
          <Modal.CustomFooter>
            <Button variant="primary" onClick={onClose}>
              {t('back')}
            </Button>
            <Button variant="outline-danger" onClick={() => handleSubmit(userId, pass, reasons, otherReason)}>
              {t('confirm')}
            </Button>
          </Modal.CustomFooter>
        </div>
      </Modal>
    </>
  );
};

export default CloseAccountModal;
